import { useMemo } from "react";

import { useSearchParams } from "@cinnamon/design-system/hooks";

import { TENANT_ID_PARAM } from "@/constants/config";
import { LOGIN_METHOD, type SSO_PROVIDER } from "@/constants/global";
import { useGetTenantPublicConfigQuery } from "@/services/tenantConfig";

export interface UseGetLoginMethodReturn {
  // Login methods: sso, traditional
  loginMethod: LOGIN_METHOD | null;
  ssoProvider: SSO_PROVIDER | null;
  isLoading: boolean;
}

interface UseGetLoginMethodProps {
  skip?: boolean;
}
const useGetLoginMethod = (props?: UseGetLoginMethodProps): UseGetLoginMethodReturn => {
  const { skip = false } = props ?? {};
  const [searchParams] = useSearchParams();
  const tenantId = useMemo(() => searchParams.get(TENANT_ID_PARAM), [searchParams]);
  const {
    data: tenantPublicConfig,
    isLoading,
    error: errorTenantPublicConfig,
  } = useGetTenantPublicConfigQuery(tenantId ?? "", {
    skip: !tenantId || skip,
  });

  return useMemo(() => {
    /*
     * No tenant ID provided, default to traditional login ]
     * or skip is true, default to traditional login
     * or error fetching tenant public config, default to traditional login
     */
    if (skip || !tenantId || errorTenantPublicConfig) {
      return {
        loginMethod: LOGIN_METHOD.TRADITIONAL,
        ssoProvider: null,
        isLoading: false,
      };
    }

    // Still loading tenant config
    if (isLoading || !tenantPublicConfig) {
      return {
        loginMethod: null,
        ssoProvider: null,
        isLoading: true,
      };
    }

    // Return the determined login method and SSO provider
    return {
      loginMethod: tenantPublicConfig.ssoEnabled ? LOGIN_METHOD.SSO : LOGIN_METHOD.TRADITIONAL,
      ssoProvider: tenantPublicConfig.ssoProvider ?? null,
      isLoading: false,
    };
  }, [skip, tenantId, errorTenantPublicConfig, isLoading, tenantPublicConfig]);
};

export default useGetLoginMethod;
